import {
  ArrowBigUp,
  ArrowDown,
  ArrowUp,
  Command,
  CornerDownLeft,
  Minus,
  MousePointerClick,
  Mouse,
  Plus,
  Settings as SettingsIcon,
  Terminal as TerminalIcon,
  X,
  Zap,
  type LucideIcon,
} from "lucide-react";

import { useEffect, useRef, useState } from "react";

import { useDialog } from "../hooks/useDialog";
import {
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
  useSettings,
  type CursorStyle,
  type DesktopConnectionMode,
} from "../stores/settingsStore";
import type { ThemeMode } from "../types";
import { Toggle } from "./Toggle";

export type SettingsSection = "general" | "terminal" | "shortcuts";

interface SettingsModalProps {
  onClose: () => void;
  theme: ThemeMode;
  setTheme: (t: ThemeMode) => void;
  accent: string;
  setAccent: (c: string) => void;
  section?: SettingsSection;
  onSectionChange: (s: SettingsSection) => void;
}

const SECTIONS: { id: SettingsSection; label: string; icon: LucideIcon }[] = [
  { id: "general", label: "General", icon: SettingsIcon },
  { id: "terminal", label: "Terminal", icon: TerminalIcon },
  { id: "shortcuts", label: "Shortcuts", icon: Zap },
];

// Mac uses ⌘; Windows/Linux use Ctrl. Prefer the Electron bridge's platform,
// fall back to the browser (matches terminalManager's detection).
const IS_MAC =
  (typeof window !== "undefined" && window.cotty?.platform === "darwin") ||
  (typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent));

// Some shortcuts only work reliably in the desktop build — browsers reserve
// Ctrl+Shift+P (Firefox/Edge private window) and pages can't intercept it.
const IS_DESKTOP = typeof window !== "undefined" && !!window.cotty;

// A single keycap: a lucide icon for modifier/special keys, else a short label.
type KeyToken = { icon: LucideIcon } | { text: string };

const MOD: KeyToken = IS_MAC ? { icon: Command } : { text: "Ctrl" };
const SHIFT: KeyToken = { icon: ArrowBigUp };
const ENTER: KeyToken = { icon: CornerDownLeft };

const SHORTCUT_GROUPS: {
  title: string;
  items: { label: string; keys: KeyToken[] }[];
}[] = [
  {
    title: "General",
    items: [
      { label: "Open command palette", keys: [MOD, { text: "K" }] },
      ...(IS_DESKTOP
        ? [{ label: "Toggle performance HUD", keys: [MOD, SHIFT, { text: "P" }] }]
        : []),
      { label: "Close dialog / menu", keys: [{ text: "Esc" }] },
    ],
  },
  {
    title: "Command palette",
    items: [
      { label: "Move selection", keys: [{ icon: ArrowUp }, { icon: ArrowDown }] },
      { label: "Open selection", keys: [ENTER] },
      { label: "Switch servers / chats", keys: [{ text: "Tab" }] },
    ],
  },
  {
    title: "Terminal",
    items: [
      {
        label: "Copy selection",
        keys: IS_MAC ? [MOD, { text: "C" }] : [MOD, SHIFT, { text: "C" }],
      },
      { label: "Paste", keys: [MOD, { text: "V" }] },
      { label: "Open link under cursor", keys: [MOD, { icon: MousePointerClick }] },
      {
        label: "Find in terminal",
        keys: IS_MAC ? [MOD, { text: "F" }] : [MOD, SHIFT, { text: "F" }],
      },
      { label: "Next / previous match", keys: [ENTER, SHIFT, ENTER] },
      { label: "Increase font size", keys: [MOD, { text: "+" }] },
      { label: "Decrease font size", keys: [MOD, { text: "−" }] },
      { label: "Reset font size", keys: [MOD, { text: "0" }] },
      { label: "Font size (pointer)", keys: [MOD, { icon: Mouse }] },
    ],
  },
  {
    title: "Chat",
    items: [
      { label: "Send message", keys: [ENTER] },
      { label: "Insert new line", keys: [SHIFT, ENTER] },
    ],
  },
];

const SECTION_HEADER =
  "m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted";
const ROW =
  "flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5 border-b border-border-faint last:border-b-0";
const ROW_TITLE = "text-[13px]";
const ROW_DESC = "mt-0.5 text-[11.5px] text-text-muted";
const SEG_BTN_BASE = "px-2.5 py-1.25 text-[11.5px] rounded-md text-text-muted";
const SEG_BTN_ACTIVE =
  "bg-bg-1 text-text shadow-[0_1px_0_rgba(255,255,255,0.05)_inset,0_1px_2px_rgba(0,0,0,0.2)] in-data-[theme=light]:shadow-[0_0_0_1px_var(--border-strong)]";

const CONNECTION_MODES: {
  id: DesktopConnectionMode;
  label: string;
  desc: string;
}[] = [
  {
    id: "direct",
    label: "Direct",
    desc: "SSH straight from this device to your hosts. No fallback.",
  },
  {
    id: "auto",
    label: "Auto",
    desc: "Try direct SSH first; fall back to the cotty server if it fails.",
  },
  {
    id: "relay",
    label: "Relay",
    desc: "Always route through the cotty server. Credentials never leave it.",
  },
];

export function SettingsModal({
  onClose, theme, setTheme, accent, setAccent,
  section = "general", onSectionChange,
}: SettingsModalProps) {
  const current = SECTIONS.find((s) => s.id === section) ?? SECTIONS[0]!;
  const panelRef = useRef<HTMLDivElement>(null);
  useDialog(panelRef, onClose);

  return (
    <div
      className="fixed inset-0 z-100 grid place-items-center bg-[rgba(5,8,14,0.6)] backdrop-blur-md animate-[hbc-fadein_0.16s_ease] in-data-[theme=light]:bg-[rgba(15,23,42,0.4)]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        className="flex flex-col md:flex-row w-190 h-135 max-w-[calc(100vw-24px)] max-h-[calc(100dvh-24px)] overflow-hidden bg-bg-1 border border-border rounded-xl shadow-(--shadow-2) animate-[hbc-slideup_0.18s_cubic-bezier(0.2,0.7,0.2,1)]">
        {/* Section nav: a left rail on desktop, a horizontal tab strip on mobile. */}
        <aside className="flex flex-row md:flex-col gap-0.5 w-full md:w-45 shrink-0 px-2 py-2 md:pt-3.5 md:pb-2 border-b border-r-0 md:border-b-0 md:border-r border-border-faint bg-bg-0 overflow-x-auto md:overflow-visible [scrollbar-width:none]">
          <div className="hidden md:block px-2.5 pt-1 pb-2.5 text-[13px] font-semibold">Settings</div>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => onSectionChange(s.id)}
              className={[
                "flex items-center gap-2 px-2.5 py-1.75 rounded-[7px] text-[12.5px] text-left shrink-0 whitespace-nowrap",
                section === s.id ? "text-text bg-bg-active" : "text-text-dim",
              ].join(" ")}
            >
              <s.icon size={13} />
              {s.label}
            </button>
          ))}
        </aside>

        <div className="flex flex-1 flex-col min-w-0">
          <div className="flex items-center gap-3 px-5 pt-4 pb-3 border-b border-border-faint flex-none">
            <h3 className="m-0 text-sm font-semibold">{current.label}</h3>
            <button
              className="ml-auto grid w-6.5 h-6.5 place-items-center rounded-md text-text-muted hover:bg-bg-hover hover:text-text"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>

          <div className="flex-1 px-5 py-4.5 overflow-y-auto">
            {section === "general" && (
              <div className="flex flex-col gap-3">
                <h4 className={SECTION_HEADER}>Appearance</h4>
                <div className={ROW}>
                  <div className="flex-1">
                    <div className={ROW_TITLE}>Theme</div>
                    <div className={ROW_DESC}>Use the OS preference, or pick a side.</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {([
                      { id: "dark" as const, label: "Dark", bg: "linear-gradient(135deg,#0a0d12,#1c222d)" },
                      { id: "light" as const, label: "Light", bg: "linear-gradient(135deg,#fff,#f4f6fa)" },
                      { id: "system" as const, label: "System", bg: "linear-gradient(90deg,#0a0d12 50%,#f4f6fa 50%)" },
                    ]).map((o) => (
                      <div
                        key={o.id}
                        className={[
                          "flex flex-col items-center gap-1.5 w-17.5 p-1.5 rounded-lg border cursor-pointer text-[11px]",
                          theme === o.id
                            ? "border-accent text-text shadow-[0_0_0_2px_var(--accent-soft)]"
                            : "border-border text-text-dim",
                        ].join(" ")}
                        onClick={() => setTheme(o.id)}
                      >
                        <div className="w-full h-9 rounded-md border border-border-faint" style={{ background: o.bg }} />
                        {o.label}
                      </div>
                    ))}
                  </div>
                </div>
                <div className={ROW}>
                  <div className="flex-1">
                    <div className={ROW_TITLE}>Accent color</div>
                    <div className={ROW_DESC}>Used for actions, status, and highlights.</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {([
                      ["#ff9344", "cotty amber"],
                      ["#2f8cff", "Blue"],
                      ["#7c5cff", "Violet"],
                      ["#22c1a5", "Teal"],
                    ] as const).map(([c, name]) => (
                      <button
                        key={c}
                        onClick={() => setAccent(c)}
                        title={name}
                        className="w-5.5 h-5.5 rounded-full"
                        style={{
                          background: c,
                          boxShadow: accent === c
                            ? `0 0 0 2px var(--bg-1), 0 0 0 4px ${c}`
                            : "0 0 0 1px var(--border)",
                        }}
                      />
                    ))}
                  </div>
                </div>

                {IS_DESKTOP && <ConnectionSettings />}
              </div>
            )}

            {section === "terminal" && <TerminalSettings />}

            {section === "shortcuts" && (
              <div className="flex flex-col gap-6">
                {SHORTCUT_GROUPS.map((group) => (
                  <div key={group.title} className="flex flex-col gap-1">
                    <h4 className={SECTION_HEADER}>{group.title}</h4>
                    {group.items.map((item) => (
                      <div key={item.label} className={ROW}>
                        <div className="flex-1">
                          <div className={ROW_TITLE}>{item.label}</div>
                        </div>
                        <div className="shrink-0 flex items-center gap-1">
                          {item.keys.map((token, i) => (
                            <KeyCap key={i} token={token} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Settings → General → Connection (desktop only). Governs how NEW terminal
 *  connections reach hosts; existing sessions pick it up on reconnect (a
 *  failing direct session re-reads it on its next attempt automatically). */
function ConnectionSettings() {
  const mode = useSettings((s) => s.desktopConnectionMode);
  const setMode = useSettings((s) => s.setDesktopConnectionMode);
  const current = CONNECTION_MODES.find((m) => m.id === mode) ?? CONNECTION_MODES[0]!;

  return (
    <>
      <h4 className={`${SECTION_HEADER} mt-3`}>Connection</h4>
      <div className={ROW}>
        <div className="flex-1">
          <div className={ROW_TITLE}>SSH connection</div>
          <div className={ROW_DESC}>{current.desc}</div>
        </div>
        <div className="inline-flex shrink-0 p-0.5 bg-bg-2 border border-border rounded-lg">
          {CONNECTION_MODES.map((m) => (
            <button
              key={m.id}
              className={`${SEG_BTN_BASE} ${mode === m.id ? SEG_BTN_ACTIVE : ""}`}
              onClick={() => setMode(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

const SCROLLBACK_OPTIONS = [1_000, 5_000, 10_000, 50_000, 100_000];

const CURSOR_STYLES: { id: CursorStyle; label: string }[] = [
  { id: "block", label: "Block" },
  { id: "bar", label: "Beam" },
  { id: "underline", label: "Underline" },
];

const STEPPER_BTN =
  "grid place-items-center w-6 h-6 rounded-md border border-border bg-bg-2 text-text-dim hover:text-text hover:border-border-strong hover:bg-bg-3 transition-colors duration-100 disabled:opacity-40 disabled:cursor-not-allowed";

/** Settings → Terminal. Every control binds to the persisted settings store;
 *  terminalManager's subscription pushes changes into live terminals as you
 *  click, so there's no save step. */
function TerminalSettings() {
  const fontSize = useSettings((s) => s.fontSize);
  const setFontSize = useSettings((s) => s.setFontSize);
  const adjustFontSize = useSettings((s) => s.adjustFontSize);
  // Free-typing draft for the number input: committing (and clamping) on every
  // keystroke would turn "12" into 8 → 24 as the digits land. Commit values in
  // range immediately; snap the display back to the stored value on blur.
  const [sizeDraft, setSizeDraft] = useState(String(fontSize));
  useEffect(() => setSizeDraft(String(fontSize)), [fontSize]);
  const scrollback = useSettings((s) => s.scrollback);
  const setScrollback = useSettings((s) => s.setScrollback);
  const cursorStyle = useSettings((s) => s.cursorStyle);
  const setCursorStyle = useSettings((s) => s.setCursorStyle);
  const copyOnSelect = useSettings((s) => s.copyOnSelect);
  const setCopyOnSelect = useSettings((s) => s.setCopyOnSelect);
  const warnMultilinePaste = useSettings((s) => s.warnMultilinePaste);
  const setWarnMultilinePaste = useSettings((s) => s.setWarnMultilinePaste);
  const perfHudEnabled = useSettings((s) => s.perfHudEnabled);
  const setPerfHudEnabled = useSettings((s) => s.setPerfHudEnabled);

  return (
    <div className="flex flex-col gap-3">
      <h4 className={SECTION_HEADER}>Font</h4>
      <div className={ROW}>
        <div className="flex-1"><div className={ROW_TITLE}>Family</div></div>
        <div className="shrink-0 font-mono text-xs">JetBrains Mono</div>
      </div>
      <div className={ROW}>
        <div className="flex-1">
          <div className={ROW_TITLE}>Size</div>
          <div className={ROW_DESC}>
            Also {IS_MAC ? "⌘" : "Ctrl"}+scroll or {IS_MAC ? "⌘" : "Ctrl"} +/− in a terminal.
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            className={STEPPER_BTN}
            aria-label="Decrease font size"
            disabled={fontSize <= FONT_SIZE_MIN}
            onClick={() => adjustFontSize(-1)}
          >
            <Minus size={12} />
          </button>
          <input
            type="number"
            min={FONT_SIZE_MIN}
            max={FONT_SIZE_MAX}
            value={sizeDraft}
            aria-label="Font size in pixels"
            onChange={(e) => {
              setSizeDraft(e.target.value);
              const v = Number(e.target.value);
              if (Number.isFinite(v) && v >= FONT_SIZE_MIN && v <= FONT_SIZE_MAX) {
                setFontSize(v);
              }
            }}
            onBlur={() => setSizeDraft(String(useSettings.getState().fontSize))}
            className="w-12 bg-bg-2 border border-border rounded-md px-1.5 py-1 font-mono text-xs text-text text-center outline-none focus:border-accent"
          />
          <span className="font-mono text-xs text-text-muted">px</span>
          <button
            className={STEPPER_BTN}
            aria-label="Increase font size"
            disabled={fontSize >= FONT_SIZE_MAX}
            onClick={() => adjustFontSize(1)}
          >
            <Plus size={12} />
          </button>
        </div>
      </div>

      <h4 className={SECTION_HEADER}>Behavior</h4>
      <div className={ROW}>
        <div className="flex-1">
          <div className={ROW_TITLE}>Scrollback</div>
          <div className={ROW_DESC}>
            Lines of history kept per terminal. Shrinking truncates existing history.
          </div>
        </div>
        <select
          value={scrollback}
          aria-label="Scrollback lines"
          onChange={(e) => setScrollback(Number(e.target.value))}
          className="shrink-0 bg-bg-2 border border-border rounded-md px-2 py-1.5 font-mono text-xs text-text outline-none focus:border-accent"
        >
          {SCROLLBACK_OPTIONS.map((n) => (
            <option key={n} value={n}>{n.toLocaleString()}</option>
          ))}
        </select>
      </div>
      <div className={ROW}>
        <div className="flex-1"><div className={ROW_TITLE}>Cursor style</div></div>
        <div className="flex gap-1.5 shrink-0">
          {CURSOR_STYLES.map((o) => (
            <button
              key={o.id}
              onClick={() => setCursorStyle(o.id)}
              className={[
                "px-2.5 py-1.5 rounded-md border text-[11.5px] transition-colors duration-100",
                cursorStyle === o.id
                  ? "border-accent text-text shadow-[0_0_0_2px_var(--accent-soft)]"
                  : "border-border text-text-dim hover:text-text hover:border-border-strong",
              ].join(" ")}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div className={ROW}>
        <div className="flex-1">
          <div className={ROW_TITLE}>Copy on select</div>
          <div className={ROW_DESC}>Selected text is copied to the clipboard automatically.</div>
        </div>
        <Toggle on={copyOnSelect} onChange={setCopyOnSelect} aria-label="Copy on select" />
      </div>
      <div className={ROW}>
        <div className="flex-1">
          <div className={ROW_TITLE}>Warn before pasting multiple lines</div>
          <div className={ROW_DESC}>Multiline pastes can run commands the moment they land.</div>
        </div>
        <Toggle
          on={warnMultilinePaste}
          onChange={setWarnMultilinePaste}
          aria-label="Warn before pasting multiple lines"
        />
      </div>
      <div className={ROW}>
        <div className="flex-1">
          <div className={ROW_TITLE}>Performance HUD</div>
          <div className={ROW_DESC}>
            Live RTT, throughput, echo latency, FPS and memory overlay.
            {IS_DESKTOP && ` Also ${IS_MAC ? "⌘" : "Ctrl"}+Shift+P.`}
          </div>
        </div>
        <Toggle
          on={perfHudEnabled}
          onChange={setPerfHudEnabled}
          aria-label="Performance HUD"
        />
      </div>
    </div>
  );
}

/** A single keyboard keycap — a lucide icon for modifier/special keys, or a
 *  short text label (e.g. a letter, "Esc", "Tab"). */
function KeyCap({ token }: { token: KeyToken }) {
  return (
    <kbd className="inline-grid place-items-center min-w-5.5 h-5.5 px-1.5 rounded bg-bg-2 border border-border font-mono text-[11px] text-text-dim">
      {"icon" in token ? <token.icon size={12} aria-hidden /> : token.text}
    </kbd>
  );
}
